<template>
  <div class="toast-container">
    <transition-group name="toast-slide" tag="div">
      <div
        v-for="notification in visibleNotifications"
        :key="notification.id"
        :class="['toast-notification', `toast-${getNotificationVariant(notification.status)}`]"
        @click="handleToastClick(notification)"
        @mouseenter="pauseAutoClose(notification.id)"
        @mouseleave="resumeAutoClose(notification.id)"
      >
        <!-- Ícone -->
        <div class="toast-icon">
          <i :class="getNotificationIcon(notification.status)"></i>
        </div>

        <!-- Conteúdo -->
        <div class="toast-content">
          <div class="toast-title">{{ notification.title }}</div>
          <div class="toast-message">{{ notification.message }}</div>
          <div v-if="notification.metadata && notification.metadata.fileName" class="toast-filename">
            <i class="el-icon-document"></i>
            {{ notification.metadata.fileName }}
          </div>
        </div>

        <!-- Botão de fechar -->
        <button class="toast-close" @click.stop="closeToast(notification.id)">
          <i class="el-icon-close"></i>
        </button>

        <!-- Barra de progresso para auto-close -->
        <div v-if="notification.autoCloseProgress" class="toast-progress">
          <div
            class="toast-progress-bar"
            :style="{ width: notification.autoCloseProgress + '%' }"
          ></div>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
export default {
  name: 'ToastNotificationStack',
  data() {
    return {
      notifications: [],
      autoCloseTimers: {},
      progressIntervals: {},
      pausedTimers: {}
    };
  },
  computed: {
    visibleNotifications() {
      // Mostra no máximo 5 notificações por vez
      return this.notifications.slice(0, 5);
    }
  },
  mounted() {
    // Escuta evento global para adicionar toasts
    this.$root.$on('show-toast', this.showToast);
  },
  beforeDestroy() {
    this.$root.$off('show-toast', this.showToast);
    // Limpa todos os timers
    Object.values(this.autoCloseTimers).forEach(timer => clearTimeout(timer));
    Object.values(this.progressIntervals).forEach(interval => clearInterval(interval));
  },
  methods: {
    showToast(notification) {
      const toast = {
        ...notification,
        id: notification.id || `toast-${Date.now()}-${Math.random()}`,
        autoCloseProgress: 100,
        createdAt: Date.now()
      };

      // Remove notificação existente com o mesmo ID (evita duplicatas)
      const existingIndex = this.notifications.findIndex(n => n.id === toast.id);
      if (existingIndex > -1) {
        // Limpa timers da notificação anterior
        if (this.autoCloseTimers[toast.id]) {
          clearTimeout(this.autoCloseTimers[toast.id]);
          delete this.autoCloseTimers[toast.id];
        }
        if (this.progressIntervals[toast.id]) {
          clearInterval(this.progressIntervals[toast.id]);
          delete this.progressIntervals[toast.id];
        }
        if (this.pausedTimers[toast.id]) {
          delete this.pausedTimers[toast.id];
        }

        // Remove do array de forma reativa
        this.notifications.splice(existingIndex, 1);
      }

      // Adiciona no início da lista
      this.notifications.unshift(toast);

      // Remove notificações antigas se passar de 10
      if (this.notifications.length > 10) {
        // Remove as mais antigas e seus timers
        const removed = this.notifications.splice(10);
        removed.forEach(n => {
          if (this.autoCloseTimers[n.id]) {
            clearTimeout(this.autoCloseTimers[n.id]);
            delete this.autoCloseTimers[n.id];
          }
          if (this.progressIntervals[n.id]) {
            clearInterval(this.progressIntervals[n.id]);
            delete this.progressIntervals[n.id];
          }
        });
      }

      // Configura auto-close (padrão 6 segundos)
      const duration = notification.duration || 6000;
      this.startAutoClose(toast.id, duration);
    },

    startAutoClose(toastId, duration) {
      // Timer para fechar
      this.autoCloseTimers[toastId] = setTimeout(() => {
        this.closeToast(toastId);
      }, duration);

      // Interval para atualizar barra de progresso
      const startTime = Date.now();
      this.progressIntervals[toastId] = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.max(0, 100 - (elapsed / duration) * 100);

        const notification = this.notifications.find(n => n.id === toastId);
        if (notification) {
          notification.autoCloseProgress = progress;
        }

        if (progress <= 0) {
          clearInterval(this.progressIntervals[toastId]);
        }
      }, 50);
    },

    pauseAutoClose(toastId) {
      // Pausa o timer quando o mouse está sobre o toast
      if (this.autoCloseTimers[toastId]) {
        clearTimeout(this.autoCloseTimers[toastId]);
        clearInterval(this.progressIntervals[toastId]);

        const notification = this.notifications.find(n => n.id === toastId);
        if (notification) {
          this.pausedTimers[toastId] = {
            remainingTime: (notification.autoCloseProgress / 100) * 6000,
            currentProgress: notification.autoCloseProgress
          };
        }
      }
    },

    resumeAutoClose(toastId) {
      // Retoma o timer quando o mouse sai
      const paused = this.pausedTimers[toastId];
      if (paused) {
        this.autoCloseTimers[toastId] = setTimeout(() => {
          this.closeToast(toastId);
        }, paused.remainingTime);

        // Retoma barra de progresso
        const startTime = Date.now();
        const startProgress = paused.currentProgress;
        this.progressIntervals[toastId] = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const progress = Math.max(0, startProgress - (elapsed / paused.remainingTime) * startProgress);

          const notification = this.notifications.find(n => n.id === toastId);
          if (notification) {
            notification.autoCloseProgress = progress;
          }

          if (progress <= 0) {
            clearInterval(this.progressIntervals[toastId]);
          }
        }, 50);

        delete this.pausedTimers[toastId];
      }
    },

    closeToast(toastId) {
      // Remove da lista
      const index = this.notifications.findIndex(n => n.id === toastId);
      if (index > -1) {
        this.notifications.splice(index, 1);
      }

      // Limpa timers
      if (this.autoCloseTimers[toastId]) {
        clearTimeout(this.autoCloseTimers[toastId]);
        delete this.autoCloseTimers[toastId];
      }
      if (this.progressIntervals[toastId]) {
        clearInterval(this.progressIntervals[toastId]);
        delete this.progressIntervals[toastId];
      }
      if (this.pausedTimers[toastId]) {
        delete this.pausedTimers[toastId];
      }
    },

    handleToastClick(notification) {
      // Ao clicar no toast, abre o SidePanel e fecha o toast
      this.$root.$emit('open-notification-panel', notification.id);
      this.closeToast(notification.id);
    },

    getNotificationVariant(status) {
      const statusMap = {
        'SUCCESS': 'success',
        'PROCESSADO': 'success',
        'ERROR': 'error',
        'ERRO': 'error',
        'WARNING': 'warning',
        'ALERTA': 'warning',
        'INFO': 'info',
        'EM_PROCESSAMENTO': 'info',
        'SOLICITADO': 'info'
      };
      return statusMap[status] || 'info';
    },

    getNotificationIcon(status) {
      const iconMap = {
        'SUCCESS': 'el-icon-success',
        'PROCESSADO': 'el-icon-success',
        'ERROR': 'el-icon-error',
        'ERRO': 'el-icon-error',
        'WARNING': 'el-icon-warning',
        'ALERTA': 'el-icon-warning',
        'INFO': 'el-icon-info',
        'EM_PROCESSAMENTO': 'el-icon-loading',
        'SOLICITADO': 'el-icon-info'
      };
      return iconMap[status] || 'el-icon-info';
    }
  }
};
</script>

<style scoped lang="scss">
.toast-container {
  position: fixed;
  top: 80px;
  right: 20px;
  z-index: 9999;
  width: 380px;
  pointer-events: none;
}

.toast-notification {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  pointer-events: auto;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(-4px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
  }

  &.toast-success::before {
    background: #67C23A;
  }

  &.toast-error::before {
    background: #F56C6C;
  }

  &.toast-warning::before {
    background: #E6A23C;
  }

  &.toast-info::before {
    background: #409EFF;
  }
}

.toast-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;

  .toast-success & {
    color: #67C23A;
  }

  .toast-error & {
    color: #F56C6C;
  }

  .toast-warning & {
    color: #E6A23C;
  }

  .toast-info & {
    color: #409EFF;
  }
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
  line-height: 1.4;
}

.toast-message {
  font-size: 13px;
  color: #606266;
  line-height: 1.4;
  word-break: break-word;
}

.toast-filename {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;

  i {
    font-size: 14px;
  }
}

.toast-close {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: #909399;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #F5F7FA;
    color: #303133;
  }

  i {
    font-size: 16px;
  }
}

.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: rgba(0, 0, 0, 0.05);
}

.toast-progress-bar {
  height: 100%;
  transition: width 0.05s linear;

  .toast-success & {
    background: #67C23A;
  }

  .toast-error & {
    background: #F56C6C;
  }

  .toast-warning & {
    background: #E6A23C;
  }

  .toast-info & {
    background: #409EFF;
  }
}

/* Animações */
.toast-slide-enter-active {
  animation: toast-slide-in 0.3s ease-out;
}

.toast-slide-leave-active {
  animation: toast-slide-out 0.3s ease-in;
}

@keyframes toast-slide-in {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes toast-slide-out {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}

/* Responsividade */
@media (max-width: 768px) {
  .toast-container {
    width: calc(100% - 40px);
    right: 20px;
    left: 20px;
  }
}
</style>
